import { cn } from "@/lib/utils";
import type { OpeningSummary, OpeningView, SearchStatus } from "@ow/shared";

// The role "health" read — one derivation and one pill treatment, shared by the home table/org chart
// and the role-detail header so a role reads identically everywhere. It's an OBSERVATION (where the
// search stands, is it slipping), never a to-do. The colour alone carries meaning — no redundant dot.

export type StatusKind = "placed" | "offer" | "final" | "risk" | "ontrack" | "sourcing" | "hold";
export type Health = { label: string; kind: StatusKind };

// One colour language, shared with the candidate-status pills so a hue means the same thing everywhere:
// blue = actively in progress (On track ↔ an active candidate); green = the win zone, BRIGHTENING as it
// closes (In final stages = faintest → Offer out → Placed = solid ↔ a placed candidate); amber = paused
// (On hold ↔ an on-hold candidate); red = slipping (At risk ↔ a rejected candidate); grey = quiet / not
// yet started (Sourcing).
const STATUS_STYLE: Record<StatusKind, string> = {
  // won — solid green, clearly terminal
  placed: "bg-[var(--color-success)] text-[color-mix(in_oklab,var(--color-success)_10%,white)]",
  // offer extended — positive green tint (the win zone)
  offer: "bg-[color-mix(in_oklab,var(--color-success)_16%,transparent)] text-[var(--color-success)]",
  // in final rounds, no offer yet — the faintest step of the green win-zone gradient ("almost there")
  final: "bg-[color-mix(in_oklab,var(--color-success)_9%,transparent)] text-[var(--color-success)]",
  // slipping — destructive red, the top-priority attention state
  risk: "bg-[color-mix(in_oklab,var(--color-destructive)_16%,transparent)] text-[var(--color-destructive)]",
  // healthy & moving — info blue (matches an active candidate)
  ontrack: "bg-[color-mix(in_oklab,var(--color-info)_18%,transparent)] text-[var(--color-info)]",
  // early, no active candidates yet — quiet neutral fill
  sourcing: "bg-foreground/[0.07] text-muted-foreground",
  // paused — warning amber, matching an on-hold candidate
  hold: "bg-[color-mix(in_oklab,var(--color-warning)_16%,transparent)] text-[var(--color-warning)]",
};

/** Attention-first ordering when sorting by status: risk/offer/late surface above steady/paused/done. */
export const STATUS_RANK: Record<StatusKind, number> = {
  risk: 0,
  offer: 1,
  final: 2,
  ontrack: 3,
  sourcing: 4,
  hold: 5,
  placed: 6,
};

/** The role's headline health, derived from the same signals the server computes for every surface. */
export function roleHealth(r: {
  status: SearchStatus;
  atOffer: boolean;
  stalled: boolean;
  nearClosure: number;
  activeCandidates: number;
}): Health {
  if (r.status === "closed") return { label: "Placed", kind: "placed" };
  if (r.status === "on_hold") return { label: "On hold", kind: "hold" };
  if (r.atOffer) return { label: "Offer out", kind: "offer" };
  if (r.stalled) return { label: "At risk", kind: "risk" };
  if (r.nearClosure > 0) return { label: "In final stages", kind: "final" };
  if (r.activeCandidates === 0) return { label: "Sourcing", kind: "sourcing" };
  return { label: "On track", kind: "ontrack" };
}

/** Status as a single scannable, colour-coded pill — the colour is the signal, so there's no dot. */
export function StatusBadge({ h, className }: { h: Health; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium",
        STATUS_STYLE[h.kind],
        className,
      )}
    >
      {h.label}
    </span>
  );
}

/** Roll the role-detail's full seat list into the same compact summary the list surfaces receive from
 *  the server — so the header chip and a row chip read identically. Cancelled seats don't count. */
export function summarizeOpenings(openings: OpeningView[]): OpeningSummary {
  let total = 0;
  let filled = 0;
  for (const o of openings) {
    if (o.status === "cancelled") continue;
    total++;
    if (o.status === "filled") filled++;
  }
  return { total, filled, open: total - filled };
}

/** A compact headcount chip for a role that fills more than one seat. Single-seat roles render nothing
 *  (the seat is implicit) so the chip only ever draws attention to genuine multi-hire mandates. The
 *  count is the real seat total; the fill breakdown rides along as a tooltip, and is shown in full on
 *  the role-detail header where there's room. */
export function OpeningsBadge({ openings, className }: { openings: OpeningSummary; className?: string }) {
  if (openings.total <= 1) return null;
  const title =
    openings.filled > 0
      ? `${openings.total} openings · ${openings.filled} filled · ${openings.open} open`
      : `${openings.total} openings`;
  return (
    <span
      title={title}
      className={cn(
        "shrink-0 rounded-full bg-[color-mix(in_oklab,var(--brand-accent)_20%,transparent)] px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-foreground",
        className,
      )}
    >
      ×{openings.total}
    </span>
  );
}
